import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, FileCheck2, Loader2, PenLine, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The employer's form, filled as it stands, for the human check (ADR 0006
 * decision 5). Shown inline in the nurse's Fast Review and the doctor's
 * Fast-Sign — not forced: the pages sit above the sign button and the doctor
 * may scroll past them.
 *
 * One call, `GET /submissions/:sid/employer-form/fill-preview`, brings every
 * page as an image plus each entry's box in thousandths of the page, so an
 * overlay is a percentage — no coordinate maths, no box dragging (the ADR's
 * not-built list). Amber = a `medium` mapping the checker should eyeball;
 * dashed = a box we are NOT filling because the item goes on the extra sheet.
 *
 * "Fix an answer" is the same inline answer editor the nurse uses, widened to
 * every question: a correction is editing the value (stored as an override in
 * `employer_form.answers`) or pulling the field off the form onto the sheet
 * (`employer_form.sheet_only`). Both go through the existing PUT.
 *
 * ADR 0010: on a form whose layout nobody has approved (`EMPLOYER_FORM_DRAW=known`)
 * the answers are only PROPOSED — drawn here, in blue dashes, but sent to the
 * extra sheet at signing. "Approve this form's layout" remembers the placements
 * for the practice's next patients with this form and lets this case's answers
 * be drawn too.
 */

const AMBER_BOX = 'border-2 border-[hsl(38_92%_50%)] bg-[hsl(45_93%_58%)]/25';
const HELD_BOX = 'border-2 border-dashed border-muted-foreground/60';
const AMBER_TEXT = 'text-[hsl(32_92%_28%)]';
const PROPOSED_BOX = 'border-2 border-dashed border-primary bg-primary/10';

function Overlay({ box, tone, title }) {
  if (!Array.isArray(box) || box.length !== 4) return null;
  const [x0, y0, x1, y1] = box;
  return (
    <div
      className={cn('absolute rounded-sm pointer-events-none', tone)}
      style={{ left: `${x0 / 10}%`, top: `${y0 / 10}%`, width: `${(x1 - x0) / 10}%`, height: `${(y1 - y0) / 10}%` }}
      title={title}
      data-testid="filled-form-overlay"
    />
  );
}

function statusOf(item, data) {
  if (item.on_sheet) return { label: 'On the sheet', tone: 'secondary' };
  if (!item.placeable) return { label: 'No box — on the sheet', tone: 'secondary' };
  const held = (data.held || []).find((h) => h.id === item.id || String(h.id).split('+').includes(item.id));
  if (held) return { label: 'On the sheet', tone: 'secondary', why: held.reason };
  if ((data.proposed || []).some((p) => p.id === item.id)) return { label: 'Proposed — approve the layout', tone: 'outline' };
  if ((data.flagged || []).some((f) => f.id === item.id)) return { label: 'Amber — glance at it', tone: 'amber' };
  if ((data.kept || []).some((k) => k.id === item.id)) return { label: 'Already on the form', tone: 'outline' };
  if ((data.filled || []).some((f) => f.id === item.id)) return { label: 'On the form', tone: 'outline' };
  return { label: '', tone: 'outline' };
}

export default function FilledFormPreview({ sid, onSaved }) {
  const [data, setData] = useState(null);      // null = loading
  const [failed, setFailed] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [edits, setEdits] = useState({});      // qid -> value typed here
  const [sheet, setSheet] = useState({});      // qid -> on the sheet?
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const r = await api.get(`/submissions/${sid}/employer-form/fill-preview`);
      setData(r.data);
    } catch {
      setData({ status: 'error' });
      setFailed(true);
    }
  }, [sid]);

  useEffect(() => {
    setData(null); setFixing(false); setEdits({}); setSheet({});
    load();
  }, [load]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.put(`/submissions/${sid}/employer-form/answers`, { answers: edits, sheet_only: sheet });
      toast.success('Form updated');
      setFixing(false); setEdits({}); setSheet({});
      onSaved?.(r.data);
      setData(null);
      await load();
    } catch (e) {
      toast.error(errText(e, 'Could not save the correction'));
    } finally { setSaving(false); }
  };

  const approve = async () => {
    if (approving) return;
    setApproving(true);
    try {
      const r = await api.post(`/submissions/${sid}/employer-form/approve-layout`);
      toast.success(`Layout approved — ${r.data.placed} answer${r.data.placed === 1 ? '' : 's'} will be filled on this form, here and for the next patients with it`);
      setData(null);
      await load();
    } catch (e) {
      toast.error(errText(e, 'Could not approve the layout'));
    } finally { setApproving(false); }
  };

  if (data === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="filled-form-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Filling in the employer's form…
      </div>
    );
  }
  if (failed || data.status === 'disabled') return null;
  if (data.status === 'not_fillable') {
    return (
      <p className="text-xs text-muted-foreground py-1" data-testid="filled-form-not-fillable">
        This form has no boxes we can fill in, so HR receives our point-by-point addendum with the original pages.
      </p>
    );
  }

  const s = data.summary || {};
  const flagged = data.flagged || [];
  const held = data.held || [];
  const proposed = data.proposed || [];
  const pending = s.verify?.pending || 0;
  const nothingEntered = !!s.nothing_entered;
  const attention = nothingEntered || flagged.length > 0 || held.length > 0;
  const changed = Object.keys(edits).length > 0 || Object.keys(sheet).length > 0;

  return (
    <div className="space-y-2" data-testid="filled-form-preview">
      <div className="flex flex-wrap items-center gap-2">
        <FileCheck2 className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">{data.form_title || 'Employer form'}, filled</span>
        <Badge variant="outline" className="text-xs" data-testid="filled-form-count">{s.filled || 0} entered</Badge>
        {flagged.length > 0 && (
          <Badge className={cn('text-xs border bg-[hsl(48_96%_92%)] border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]', AMBER_TEXT)} data-testid="filled-form-flagged">
            {flagged.length} amber
          </Badge>
        )}
        {held.length > 0 && <Badge variant="secondary" className="text-xs" data-testid="filled-form-held">{held.length} on the sheet</Badge>}
        {proposed.length > 0 && <Badge variant="outline" className="text-xs border-primary text-primary" data-testid="filled-form-proposed">{proposed.length} proposed</Badge>}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={() => setFixing((v) => !v)} data-testid="filled-form-fix-button">
          <PenLine className="h-3.5 w-3.5 mr-1.5" /> {fixing ? 'Close' : 'Fix an answer'}
        </Button>
      </div>

      {attention ? (
        <div className={cn('rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] px-3 py-2 text-xs flex items-start gap-2', AMBER_TEXT)} data-testid="filled-form-banner">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>
            {nothingEntered ? (
              <span data-testid="filled-form-nothing-entered">
                As it stands nothing can be entered on the form itself, so HR would receive our point-by-point addendum with the original pages. Fix an answer to place entries on the form.{' '}
              </span>
            ) : (
              <>
                {flagged.length > 0 && 'Amber boxes are entries we are less sure of — glance at them before signing. '}
                {held.length > 0 && `${held.length} item${held.length === 1 ? '' : 's'} could not be entered on the form and go on the one extra sheet behind it. `}
              </>
            )}
            {pending > 0 && 'Placement on a photographed page is checked again at signing; anything that fails moves to the sheet.'}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="filled-form-all-clear">
          <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(158_64%_32%)]" /> Every answer landed in its box.
          {pending > 0 && ' Placement is checked once more at signing.'}
        </p>
      )}

      {proposed.length > 0 && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="filled-form-approve-banner">
          <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="flex-1 min-w-[12rem]">
            We have not filled this form before. The {proposed.length} answer{proposed.length === 1 ? '' : 's'} in blue dashes show where they would go; until someone approves the layout they go on the extra sheet. Pull anything misplaced with Fix an answer, then approve — the next patient with this form is filled automatically.
          </span>
          <Button size="sm" variant="outline" disabled={approving} onClick={approve} data-testid="filled-form-approve">
            {approving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />} Approve this form's layout
          </Button>
        </div>
      )}

      {fixing && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-3" data-testid="filled-form-editor">
          <p className="text-xs text-muted-foreground">
            Change an answer, or move an item onto the extra sheet instead of the form. The form is filled again as soon as you save.
          </p>
          <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
            {(data.items || []).map((it) => {
              const st = statusOf(it, data);
              const value = edits[it.id] ?? it.answer ?? '';
              const onSheet = sheet[it.id] ?? it.on_sheet;
              return (
                <div key={it.id} className="space-y-1" data-testid={`filled-form-item-${it.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm">{it.question}</span>
                    {st.label && (
                      <Badge
                        variant={st.tone === 'amber' ? undefined : st.tone}
                        className={cn('text-xs', st.tone === 'amber' && cn('border bg-[hsl(48_96%_92%)] border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]', AMBER_TEXT))}
                        title={st.why || ''}
                      >
                        {st.label}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
                    {it.answer_type === 'yes_no' ? (
                      <Select value={value} onValueChange={(v) => setEdits((e) => ({ ...e, [it.id]: v }))}>
                        <SelectTrigger className="h-9" data-testid={`filled-form-answer-${it.id}`}><SelectValue placeholder="Yes or no" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="h-9"
                        type={it.answer_type === 'date' ? 'date' : 'text'}
                        placeholder={it.source === 'unanswered' ? 'Not answered — goes on the sheet as such' : 'Answer'}
                        value={value}
                        onChange={(e) => setEdits((ed) => ({ ...ed, [it.id]: e.target.value }))}
                        data-testid={`filled-form-answer-${it.id}`}
                      />
                    )}
                    {it.placeable && (
                      <label className="flex items-center gap-2 text-xs text-muted-foreground whitespace-nowrap">
                        <Checkbox
                          checked={!!onSheet}
                          onCheckedChange={(v) => setSheet((sh) => ({ ...sh, [it.id]: !!v }))}
                          data-testid={`filled-form-sheet-${it.id}`}
                        />
                        On the sheet, not the form
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setFixing(false); setEdits({}); setSheet({}); }} data-testid="filled-form-cancel">Cancel</Button>
            <Button size="sm" disabled={!changed || saving} onClick={save} data-testid="filled-form-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null} Save and refill
            </Button>
          </div>
        </div>
      )}

      <div className="max-h-[60vh] overflow-auto rounded-lg border bg-muted/40 p-2 space-y-2" data-testid="filled-form-pages">
        {(data.pages || []).map((b64, i) => (
          <div key={i} className="relative bg-white rounded shadow-sm" data-testid={`filled-form-page-${i}`}>
            <img src={`data:image/jpeg;base64,${b64}`} alt={`Employer form, page ${i + 1}, filled`} className="w-full h-auto block rounded" />
            {proposed.filter((e) => e.page === i + 1).map((e) => (
              <Overlay key={`p-${e.id}`} box={e.box} tone={PROPOSED_BOX} title="Proposed: approve the layout to fill it" />
            ))}
            {flagged.filter((e) => e.page === i + 1).map((e) => (
              <Overlay key={`f-${e.id}`} box={e.box} tone={AMBER_BOX} title="Glance at this entry" />
            ))}
            {held.filter((e) => e.page === i + 1 && e.box).map((e) => (
              <Overlay key={`h-${e.id}`} box={e.box} tone={HELD_BOX} title={`On the sheet: ${e.reason || ''}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
